import { useState } from 'react';
import { useShow, useDelete, useInvalidate } from '@refinedev/core';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PageHeader } from '@/components/PageHeader';
import { FieldList } from '@/components/FieldList';
import { hub } from '@/lib/hub-client';
import { fmtIso } from '@/lib/format';

interface PromptVersion {
  id: string;
  version: number;
  content: string;
  inputs: string[];
  createdBy: string | null;
  createdAt: string;
}

interface PromptDetail {
  id: string;
  trackId: string;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  latestVersion: PromptVersion | null;
}

export function PromptsShow() {
  const navigate = useNavigate();
  const { query } = useShow<PromptDetail>();
  const prompt = query.data?.data;
  const invalidate = useInvalidate();
  const { mutate: deletePrompt, mutation: deleteMutation } = useDelete();
  const deleting = deleteMutation.isPending;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [newContent, setNewContent] = useState('');
  const [newInputs, setNewInputs] = useState('');
  const [savingVersion, setSavingVersion] = useState(false);
  const [versionError, setVersionError] = useState<string | null>(null);

  if (query.isLoading) return <div>Loading…</div>;
  if (!prompt) return <div>Not found.</div>;

  const onDelete = () =>
    deletePrompt(
      { resource: 'prompts', id: prompt.id },
      { onSuccess: () => navigate('/admin/prompts') },
    );

  const saveVersion = async (e: React.FormEvent) => {
    e.preventDefault();
    setVersionError(null);
    setSavingVersion(true);
    try {
      const inputs = newInputs.split(',').map((s) => s.trim()).filter(Boolean);
      const body: { content: string; inputs?: string[] } = { content: newContent };
      if (inputs.length) body.inputs = inputs;
      await hub.promptsCreateVersion({ params: { id: prompt.id }, body });
      setNewContent('');
      setNewInputs('');
      await invalidate({ resource: 'prompts', id: prompt.id, invalidates: ['detail'] });
    } catch (err) {
      setVersionError((err as Error).message);
    } finally {
      setSavingVersion(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={prompt.name}
        description={prompt.description ?? '—'}
        actions={[
          { label: 'Edit', to: `/admin/prompts/edit/${prompt.id}`, variant: 'outline' },
          { label: 'Delete', variant: 'destructive', onClick: () => setConfirmDelete(true), disabled: deleting },
        ]}
      />
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Metadata</CardTitle>
        </CardHeader>
        <CardContent>
          <FieldList
            fields={[
              { label: 'ID', value: prompt.id, mono: true },
              { label: 'Track', value: prompt.trackId, mono: true },
              { label: 'Created', value: fmtIso(prompt.createdAt) },
              { label: 'Updated', value: fmtIso(prompt.updatedAt) },
            ]}
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Latest version {prompt.latestVersion ? `· v${prompt.latestVersion.version}` : '· —'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {prompt.latestVersion ? (
            <>
              <pre className="overflow-x-auto rounded-md border bg-muted/50 px-3 py-2 font-mono text-xs">
                {prompt.latestVersion.content}
              </pre>
              <div className="text-xs text-muted-foreground">
                Inputs: {prompt.latestVersion.inputs.length ? prompt.latestVersion.inputs.join(', ') : '—'}
                {' · '}
                Created: {fmtIso(prompt.latestVersion.createdAt)}
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No versions yet.</p>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Add new version</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-3" onSubmit={saveVersion}>
            <textarea
              className="min-h-32 w-full rounded-md border bg-background px-3 py-2 font-mono text-xs"
              placeholder="Prompt content (Mustache-style {{inputs}} allowed)"
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              required
            />
            <input
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              placeholder="Inputs (comma separated)"
              value={newInputs}
              onChange={(e) => setNewInputs(e.target.value)}
            />
            {versionError && <p className="text-sm text-destructive">{versionError}</p>}
            <Button type="submit" disabled={savingVersion}>
              {savingVersion ? 'Saving…' : 'Save version'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete prompt?"
        description={`"${prompt.name}" and all of its versions will be removed.`}
        confirmLabel="Delete"
        destructive
        busy={deleting}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={onDelete}
      />
    </div>
  );
}
